'use client';

import { ChevronDown, Lightbulb, Mic, Paperclip, Send } from 'lucide-react';
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/Input';
import { cn } from '../../../lib/utils';

/**
 * KeenAI-Style Chat Input Component
 * Exact recreation: Expert dropdown, attachments, microphone, message input
 */
export function KeenAIChatInput({
  value = '',
  onChange,
  onSend,
  placeholder = 'Message...',
  disabled = false,
}) {
  const [message, setMessage] = useState(value);
  const [selectedExpert, setSelectedExpert] = useState('Expert');

  const experts = [
    'Expert',
    'Developer',
    'Business',
    'Educator',
    'Creative',
  ];

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);
    onChange?.(newValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && onSend) {
      onSend(message.trim());
      setMessage('');
      onChange?.('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <form onSubmit={handleSubmit}>
        {/* Unified Input Field - Large container with everything inside */}
        <div className="flex items-center w-full border-2 border-gray-300 rounded-xl px-4 py-3.5 bg-white focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-200 transition-all min-h-[56px]">
          {/* Expert Dropdown - Inside input on left */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors shrink-0 border-r border-gray-200 pr-3 mr-2"
              >
                <Lightbulb className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{selectedExpert}</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {experts.map((expert) => (
                <DropdownMenuItem
                  key={expert}
                  onClick={() => setSelectedExpert(expert)}
                  className={cn(
                    'cursor-pointer',
                    selectedExpert === expert && 'bg-purple-50 text-purple-700'
                  )}
                >
                  {expert}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Message Input - Flex-1 to take remaining space */}
          <input
            type="text"
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 outline-none bg-transparent text-base text-gray-900 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          />

          {/* Attachment Button - Inside input on right */}
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0 mr-1"
            title="Attach file"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          {/* Microphone Button - Inside input on far right */}
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            title="Voice input"
          >
            <Mic className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}

export default KeenAIChatInput;

